export interface Notification {
  id: number;
  userId: number;
  entityType: string;
  entityId: number;
  message: string;
  link?: string;
  createdAt: string;
  readAt?: string;
}

export type NotificationType = 'task' | 'ticket' | 'reminder' | 'appointment' | 'invoice' | 'client' | 'other';

export interface NotificationTypeConfig {
  icon: string;
  color: string;
  label: string;
}

export const NOTIFICATION_TYPES: Record<NotificationType, NotificationTypeConfig> = {
  task: {
    icon: 'check-circle',
    color: 'text-blue-600',
    label: 'Task',
  },
  ticket: {
    icon: 'ticket',
    color: 'text-orange-600',
    label: 'Support Ticket',
  },
  reminder: {
    icon: 'clock',
    color: 'text-yellow-600',
    label: 'Reminder',
  },
  appointment: {
    icon: 'calendar',
    color: 'text-green-600',
    label: 'Appointment',
  },
  invoice: {
    icon: 'document',
    color: 'text-purple-600',
    label: 'Invoice',
  },
  client: {
    icon: 'user-group',
    color: 'text-indigo-600',
    label: 'Client',
  },
  other: {
    icon: 'bell',
    color: 'text-gray-600',
    label: 'Notification',
  },
};

export function getNotificationType(entityType: string): NotificationType {
  const lowerType = entityType.toLowerCase();
  if (lowerType.includes('task')) return 'task';
  if (lowerType.includes('ticket')) return 'ticket';
  if (lowerType.includes('reminder')) return 'reminder';
  if (lowerType.includes('appointment')) return 'appointment';
  if (lowerType.includes('invoice')) return 'invoice';
  if (lowerType.includes('client')) return 'client';
  return 'other';
}

export function formatNotificationDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

  return date.toLocaleDateString();
}

export function groupNotificationsByDate(notifications: Notification[]): {
  today: Notification[];
  yesterday: Notification[];
  thisWeek: Notification[];
  older: Notification[];
} {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);

  const groups = {
    today: [] as Notification[],
    yesterday: [] as Notification[],
    thisWeek: [] as Notification[],
    older: [] as Notification[],
  };

  notifications.forEach((notification) => {
    const notifDate = new Date(notification.createdAt);
    if (notifDate >= today) {
      groups.today.push(notification);
    } else if (notifDate >= yesterday) {
      groups.yesterday.push(notification);
    } else if (notifDate >= weekAgo) {
      groups.thisWeek.push(notification);
    } else {
      groups.older.push(notification);
    }
  });

  return groups;
}
